/**
 * DRY registry: action type → metadata + field definitions.
 * Adding a new BE action type = add one entry here, no layout changes needed.
 *
 * Real types today:
 *  - swift_blacklist         (destructive, edge IP block)
 *  - alibaba_blacklist       (destructive, CDN edge block)
 *  - notify_oncall           (non-destructive, channel notification)
 *  - check_linked_services   (non-destructive, diagnostic health check)
 *  - send_webhook            (non-destructive, templated HTTP webhook)
 */
import { ShieldBan, BellRing, Activity, Send } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type FieldType =
  | 'number'
  | 'text'
  | 'channel-select'
  | 'severity-select'
  | 'watchpoint-multiselect'
  | 'webhook-url'
  | 'http-method'
  | 'headers-editor'
  | 'body-template';

export interface ActionFieldDef {
  name: string;
  label: string;
  type: FieldType;
  /**
   * Scalar default for simple fields.
   * Use [] for watchpoint-multiselect.
   * Use {} for headers-editor.
   */
  default: string | number | string[] | Record<string, string>;
  required?: boolean;
  min?: number;
}

export interface ActionTypeMeta {
  label: string;
  icon: LucideIcon;
  description: string;
  /** Present → show red blast-radius banner. */
  warning?: string;
  /** true = show red blast-radius banner; false = info tone. */
  destructive: boolean;
  fields: ActionFieldDef[];
}

export type PlaybookActionType =
  | 'swift_blacklist'
  | 'alibaba_blacklist'
  | 'notify_oncall'
  | 'check_linked_services'
  | 'send_webhook';

export const ACTION_META: Record<PlaybookActionType, ActionTypeMeta> = {
  swift_blacklist: {
    label: 'Swift IP Blacklist',
    icon: ShieldBan,
    description: 'Blocks the offending IP at the Swift edge.',
    warning:
      'Blocks live traffic from matched IPs. Affects real users if misconfigured. This action is effectively irreversible until the block duration ends.',
    destructive: true,
    fields: [
      { name: 'duration', label: 'Duration (seconds)', type: 'number', default: 3600, required: true, min: 0 },
      { name: 'reason', label: 'Reason', type: 'text', default: 'threshold_triggered', required: true },
    ],
  },
  alibaba_blacklist: {
    label: 'Alibaba CDN Blacklist',
    icon: ShieldBan,
    description: 'Blacklists the IP at Alibaba CDN edge.',
    warning: 'Applies a CDN-edge block. Region-scoped — affects live delivery for all users behind that edge.',
    destructive: true,
    fields: [
      { name: 'region', label: 'Region', type: 'text', default: 'cn-hangzhou', required: true },
    ],
  },
  notify_oncall: {
    label: 'Notify On-Call',
    icon: BellRing,
    description: 'Sends a notification to an on-call channel when the alarm fires.',
    destructive: false,
    fields: [
      { name: 'channel_id', label: 'Channel', type: 'channel-select', default: '', required: true },
      { name: 'severity', label: 'Severity', type: 'severity-select', default: 'critical', required: false },
      { name: 'message', label: 'Message (optional)', type: 'text', default: '' },
    ],
  },
  check_linked_services: {
    label: 'Check Linked Services',
    icon: Activity,
    description:
      'Reads the latest health of the selected monitors and sends a diagnostic summary to a channel.',
    destructive: false,
    fields: [
      { name: 'channel_id', label: 'Send report to', type: 'channel-select', default: '', required: true },
      { name: 'watchpoint_ids', label: 'Monitors to check', type: 'watchpoint-multiselect', default: [], required: true },
    ],
  },
  send_webhook: {
    label: 'Send Webhook',
    icon: Send,
    description: 'POST a custom payload built from the alert\'s params to any URL.',
    destructive: false,
    fields: [
      { name: 'url', label: 'URL', type: 'webhook-url', default: '', required: true },
      { name: 'method', label: 'Method', type: 'http-method', default: 'POST', required: false },
      { name: 'headers', label: 'Headers (optional)', type: 'headers-editor', default: {}, required: false },
      { name: 'body_template', label: 'Body template (optional)', type: 'body-template', default: '', required: false },
    ],
  },
} as const;

// Derived from ACTION_META keys so it can never drift out of sync.
// Order: non-destructive first → destructive last (so the default selection is safe).
const _ORDER: Record<PlaybookActionType, number> = {
  notify_oncall: 0,
  check_linked_services: 1,
  send_webhook: 2,
  swift_blacklist: 3,
  alibaba_blacklist: 4,
};

export const ACTION_TYPE_OPTIONS: PlaybookActionType[] = (
  Object.keys(ACTION_META) as PlaybookActionType[]
).sort((a, b) => (_ORDER[a] ?? 99) - (_ORDER[b] ?? 99));

/** Build a default config object for a given action type. */
export function defaultConfig(type: PlaybookActionType): Record<string, unknown> {
  const meta = ACTION_META[type];
  const config: Record<string, unknown> = {};
  for (const field of meta.fields) {
    if (Array.isArray(field.default)) {
      // Array defaults must be cloned, not shared by reference.
      config[field.name] = [];
    } else if (field.default !== null && typeof field.default === 'object') {
      // Object defaults (e.g. headers) must be cloned too.
      config[field.name] = {};
    } else {
      config[field.name] = field.default;
    }
  }
  return config;
}
